import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Clock } from 'lucide-react';
import { ICampaign, ISequenceStep, SequenceStepType } from '../../../../interfaces/models/campaign';
import { campaignMobile } from '../../utils/campaignBreakpoints';
import SvgFragment from '../../../../fragments/SvgFragment';
import { STEP_META } from './CampaignLeadSidebar';
import { formatStepDelay } from '../../../../utils/campaignSequenceSummary';

// What each step does to a lead, written for someone reading the campaign, not building it.
const STEP_EXPLANATIONS: Record<SequenceStepType, string> = {
  connection_request: 'Sends a LinkedIn connection request to the lead.',
  message: 'Sends a LinkedIn message. Only leads who accepted the connection request get it.',
  sn_message: 'Reaches leads you are not connected with: free for Open Profiles, otherwise it uses an InMail credit.',
  profile_visit: 'Views the lead\'s LinkedIn profile, so they see you among their profile viewers.',
  post_like: 'Likes the lead\'s latest post. Skipped when they have no recent post.',
  post_comment: 'Comments on the lead\'s latest post. Skipped when they have no recent post.',
  email: 'Emails the lead from the sender\'s own mailbox. Needs the lead\'s email address.',
};

// Mirrors the executor: a delay runs from the last thing that happened to the lead.
function timingText(step: ISequenceStep, previous?: ISequenceStep): string {
  const afterAccept = step.type === 'message' && previous?.type === 'connection_request';
  const delay = formatStepDelay(step);
  if (!previous) return delay ? `${delay} after the lead is added` : 'As soon as the lead is added';
  if (afterAccept) return delay ? `${delay} after the invite is accepted` : 'Once the invite is accepted';
  return delay ? `${delay} after the previous step` : 'Right after the previous step';
}

interface ICampaignSequenceProps {
  campaign: ICampaign;
}

const CampaignSequence: FunctionComponent<ICampaignSequenceProps> = ({ campaign }) => {
  const sequence = campaign.sequence ?? [];

  return (
    <Panel>
      <Header>
        <PanelTitle>Campaign sequence</PanelTitle>
        <Subtitle>The steps every lead goes through, in order. A lead leaves the sequence as soon as they reply.</Subtitle>
      </Header>

      {sequence.length ? (
        <Steps>
          {sequence.map((step, i) => {
            const meta = STEP_META[step.type];
            return (
              <Step key={i}>
                <Rail>
                  <StepIcon $color={meta.color} $bg={meta.bg}><SvgFragment type={meta.icon} /></StepIcon>
                  {i < sequence.length - 1 && <RailLine />}
                </Rail>
                <StepBody>
                  <StepTitleRow>
                    <StepNumber>Step {i + 1}</StepNumber>
                    <StepTitle>{meta.label}</StepTitle>
                  </StepTitleRow>
                  <StepText>{STEP_EXPLANATIONS[step.type]}</StepText>
                  <Timing>
                    <Clock size={14} strokeWidth={1.5} />
                    {timingText(step, sequence[i - 1])}
                  </Timing>
                </StepBody>
              </Step>
            );
          })}
        </Steps>
      ) : (
        <Muted>No steps configured</Muted>
      )}
    </Panel>
  );
};

export default CampaignSequence;

const Panel = styled.div`
  background: #FFFFFF;
  border: 1px solid #E9EAEB; border-radius: 12px;
  padding: 24px;
  display: flex; flex-direction: column; gap: 20px;
  ${campaignMobile`padding: 16px;`}
`;

const Header = styled.div`display: flex; flex-direction: column; gap: 4px;`;

const PanelTitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 20px; font-weight: 700; line-height: 30px;
  color: #111927;
`;

const Subtitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; line-height: 20px; color: #6B7280;
`;

const Steps = styled.ol`display: flex; flex-direction: column; margin: 0; padding: 0; list-style: none;`;

const Step = styled.li`display: flex; gap: 12px;`;

// The icon column; its line joins each step to the next.
const Rail = styled.div`display: flex; flex-direction: column; align-items: center; flex-shrink: 0;`;

const RailLine = styled.span`flex: 1; width: 1px; min-height: 12px; margin: 4px 0; background: #E5E7EB;`;

// Same icon tiles as the lead sidebar's sequence, sized up for a page.
const StepIcon = styled.span<{ $color: string; $bg: string }>`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 32px; height: 32px; border-radius: 8px;
  background: ${({ $bg }) => $bg};
  .svg { width: 16px; height: 16px; }
  .path { stroke: ${({ $color }) => $color}; fill: none; }
`;

const StepBody = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 0; padding: 4px 0 20px;`;

const StepTitleRow = styled.div`display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px;`;

const StepNumber = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 12px; font-weight: 600; line-height: 18px;
  color: #6B7280; text-transform: uppercase; letter-spacing: 0.02em;
`;

const StepTitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  color: #252B37;
`;

const StepText = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; line-height: 20px; color: #414651;
`;

const Timing = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 12px; line-height: 18px; color: #6B7280;
  svg { flex-shrink: 0; color: #8C96AD; }
`;

const Muted = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; color: #6B7280;
`;
